import { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Controller, useController } from 'react-hook-form';
import { Venues } from 'types/generated/client';
import {
  MAX_END_TIME_MINUTES,
  MINIMUM_TIME_FROM_START_MINUTES,
  VenueAddress,
  VenueSourceType,
  hourOptions,
} from 'utils/shared/coachBuilder';
import { is24HourScale } from 'utils/shared/time/is24HourScale';
import CalendarAddWhite from 'svg/CalendarAddWhite';
import CheckIcon from 'svg/CheckIcon';
import ChevronDown from 'svg/ChevronDown';
import PlusPlain from 'svg/PlusPlain';
import classNames from 'styles/utils/classNames';

interface ScheduleItemProps {
  venues: VenueAddress[];
  onAddVenue: () => void;
  venueFieldName: string;
  startTimeFieldName: string;
  endTimeFieldName: string;
  control: any;
  handleSameScheduleForMultipleDays?: boolean;
  invert?: boolean;
  onChangeMultipleDays?: (
    startTimeMinutes: number,
    endTimeMinutes: number,
    venueId: string,
  ) => void;
}

const ScheduleItem = ({
  onAddVenue,
  venues,
  venueFieldName,
  startTimeFieldName,
  endTimeFieldName,
  control,
  handleSameScheduleForMultipleDays,
  onChangeMultipleDays,
  invert,
}: ScheduleItemProps) => {
  const {
    field: startTimeField,
    fieldState: { error: startTimeFieldError },
  } = useController({
    name: startTimeFieldName,
    control,
  });

  const { field: endTimeField } = useController({
    name: endTimeFieldName,
    control,
  });

  const {
    field: venueField,
    fieldState: { error: venueFieldError },
  } = useController({
    name: venueFieldName,
    control,
  });

  const startTime = hourOptions.find(
    (option) => option.minutesAfterMidnight === startTimeField.value,
  );
  const endTime = hourOptions.find((option) => option.minutesAfterMidnight === endTimeField.value);
  const displayStartTime = is24HourScale() ? startTime?.display24 : startTime?.display12;
  const displayEndTime = is24HourScale() ? endTime?.display24 : endTime?.display12;
  const selectedVenue = venues?.find((venue) => venue.id === venueField.value);
  const displayVenueTitle = selectedVenue?.address || 'Select Location';

  const adjustedStartTimeList = hourOptions.filter((option) => {
    return option.minutesAfterMidnight <= MAX_END_TIME_MINUTES; //  MAX_END_TIME_MINUTES is 23:00
  });

  // Minimum one hour gap (60 minutes) from start time
  const adjustedEndTimeList = hourOptions.filter((option) => {
    return (
      option.minutesAfterMidnight > (startTimeField.value || 0) &&
      option.minutesAfterMidnight <= 1440 &&
      option.minutesAfterMidnight - (startTimeField.value || 0) >= MINIMUM_TIME_FROM_START_MINUTES
    );
  });

  const handleStartTimeChange = (minutesAfterMidnight: number) => {
    let newEndTime = endTimeField.value;

    // If the start time is greater than or equal to the current end time, adjust the end time
    if (minutesAfterMidnight >= newEndTime) {
      newEndTime = minutesAfterMidnight + MINIMUM_TIME_FROM_START_MINUTES;
      endTimeField.onChange(newEndTime);
    } else {
      // Check the difference between the start time and the current end time
      const timeDifference = newEndTime - minutesAfterMidnight;
      if (timeDifference < MINIMUM_TIME_FROM_START_MINUTES) {
        newEndTime = newEndTime + (MINIMUM_TIME_FROM_START_MINUTES - timeDifference);
        endTimeField.onChange(newEndTime);
      }
    }

    startTimeField.onChange(minutesAfterMidnight);
    if (handleSameScheduleForMultipleDays && onChangeMultipleDays) {
      onChangeMultipleDays(minutesAfterMidnight, newEndTime, venueField.value);
    }
  };

  const handleEndTimeChange = (minutesAfterMidnight: number) => {
    let newStartTime = startTimeField.value;

    if (minutesAfterMidnight <= newStartTime) {
      newStartTime = minutesAfterMidnight - MINIMUM_TIME_FROM_START_MINUTES;
      startTimeField.onChange(newStartTime);
    }

    endTimeField.onChange(minutesAfterMidnight);
    if (handleSameScheduleForMultipleDays && onChangeMultipleDays) {
      onChangeMultipleDays(newStartTime, minutesAfterMidnight, venueField.value);
    }
  };

  const handleVenueChange = (venueId: string) => {
    if (!venueId) return; // to handle add location button
    venueField.onChange(venueId);
    if (handleSameScheduleForMultipleDays && onChangeMultipleDays) {
      onChangeMultipleDays(startTimeField.value, endTimeField.value, venueId);
    }
  };

  return (
    <div className="flex min-w-0 flex-1 max-sm:flex-col max-sm:gap-3">
      <div className="flex flex-col gap-2 ">
        <div className="flex items-center ">
          <div
            className={classNames('relative', is24HourScale() ? 'w-24' : 'w-28', 'max-sm:flex-1')}
          >
            <Controller
              name={startTimeFieldName}
              control={control}
              render={({ field }) => (
                <Listbox value={field.value} onChange={handleStartTimeChange}>
                  {({ open }) => (
                    <>
                      <div className="relative">
                        <Listbox.Button
                          className={classNames(
                            'relative min-h-10 w-full',
                            startTimeFieldError &&
                              'bg-color-bg-lightmode-error ring ring-color-bg-darkmode-icon-error dark:bg-color-bg-darkmode-error',
                            !startTimeFieldError && 'input-form',
                            invert && '!bg-color-text-lightmode-primary',
                            invert &&
                              open &&
                              '!bg-color-bg-lightmode-invert shadow-[0px_0px_0px_2px_#0F0F10,_0px_0px_0px_3px_#3B3A40]',
                          )}
                        >
                          <span
                            className={classNames(
                              'block',
                              !displayStartTime &&
                                !startTimeFieldError &&
                                'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                              startTimeFieldError && 'text-color-text-lightmode-error',

                              invert &&
                                (open || !!displayStartTime) &&
                                'text-color-text-lightmode-invert',
                            )}
                          >
                            {displayStartTime || 'start time'}
                          </span>
                        </Listbox.Button>

                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options
                            className={classNames(
                              'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary sm:text-sm',
                              invert &&
                                '!bg-color-bg-darkmode-primary shadow-[0px_4px_20px_0px_rgba(255,255,255,0.16)]',
                            )}
                          >
                            {adjustedStartTimeList.map((time) => (
                              <Listbox.Option
                                key={time.id}
                                className={({ active }) =>
                                  classNames(
                                    active ? 'bg-black text-white' : 'text-gray-900',
                                    'relative cursor-pointer select-none px-4 py-2',
                                    invert && '!text-color-text-lightmode-invert',
                                  )
                                }
                                value={time.minutesAfterMidnight}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={classNames('block')}>
                                      {is24HourScale() ? time.display24 : time.display12}
                                    </span>

                                    {selected ? (
                                      <span
                                        className={classNames(
                                          'absolute inset-y-0 right-0 flex items-center pr-1',
                                          active ? 'text-white' : 'text-black',
                                          invert && '!text-white',
                                        )}
                                      >
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </>
                  )}
                </Listbox>
              )}
            />
          </div>
          <div className="mx-2">-</div>
          <div
            className={classNames('relative', is24HourScale() ? 'w-24' : 'w-28', 'max-sm:flex-1')}
          >
            <Controller
              name={endTimeFieldName}
              control={control}
              render={({ field }) => (
                <Listbox value={field.value} onChange={handleEndTimeChange}>
                  {({ open }) => (
                    <>
                      <div className="relative">
                        <Listbox.Button
                          className={classNames(
                            'relative min-h-10 w-full',
                            startTimeFieldError &&
                              '  bg-color-bg-lightmode-error ring ring-color-bg-darkmode-icon-error dark:bg-color-bg-darkmode-error',
                            !startTimeFieldError && 'input-form',
                            invert && '!bg-color-text-lightmode-primary',
                            invert &&
                              open &&
                              '!bg-color-bg-lightmode-invert shadow-[0px_0px_0px_2px_#0F0F10,_0px_0px_0px_3px_#3B3A40]',
                          )}
                        >
                          <span
                            className={classNames(
                              'block',
                              !displayEndTime &&
                                !startTimeFieldError &&
                                'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                              startTimeFieldError && 'text-color-text-lightmode-error',
                              invert &&
                                (open || displayEndTime) &&
                                'text-color-text-lightmode-invert',
                            )}
                          >
                            {displayEndTime || 'end time'}
                          </span>
                        </Listbox.Button>

                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options
                            className={classNames(
                              'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary sm:text-sm',
                              invert &&
                                '!bg-color-bg-darkmode-primary shadow-[0px_4px_20px_0px_rgba(255,255,255,0.16)]',
                            )}
                          >
                            {adjustedEndTimeList.map((time) => (
                              <Listbox.Option
                                key={time.id}
                                className={({ active }) =>
                                  classNames(
                                    active ? 'bg-black text-white' : 'text-gray-900',
                                    'relative cursor-pointer select-none px-4 py-2',
                                    invert && '!text-color-text-lightmode-invert',
                                  )
                                }
                                value={time.minutesAfterMidnight}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={classNames('block')}>
                                      {is24HourScale() ? time.display24 : time.display12}
                                    </span>

                                    {selected ? (
                                      <span
                                        className={classNames(
                                          'absolute inset-y-0 right-0 flex items-center pr-1',
                                          active ? 'text-white' : 'text-black',
                                          invert && '!text-white',
                                        )}
                                      >
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </>
                  )}
                </Listbox>
              )}
            />
          </div>
        </div>
        {startTimeFieldError && (
          <span className="typography-product-text-card text-color-error">
            {startTimeFieldError?.message}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1">
        <span className="typography-product-body mx-2 mt-2 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
          at
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Controller
            name={venueFieldName}
            control={control}
            render={({ field }) => (
              <Listbox value={field.value} onChange={handleVenueChange}>
                {({ open }) => (
                  <>
                    <div className="relative min-w-0 flex-1">
                      <Listbox.Button
                        className={classNames(
                          'relative min-h-10 w-full',
                          venueFieldError &&
                            'input-form bg-color-bg-lightmode-error ring ring-color-bg-darkmode-icon-error dark:bg-color-bg-darkmode-error',
                          !venueFieldError && 'input-form',
                          invert && '!bg-color-text-lightmode-primary',
                          invert &&
                            open &&
                            '!bg-color-bg-lightmode-invert shadow-[0px_0px_0px_2px_#0F0F10,_0px_0px_0px_3px_#3B3A40]',
                        )}
                      >
                        {venues?.length === 0 ? (
                          <span
                            onClick={onAddVenue}
                            className={
                              'typography-product-button-label-medium flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-color-brand-primary'
                            }
                          >
                            <PlusPlain className="h-5 w-5 shrink-0 " /> Add Location
                          </span>
                        ) : (
                          <div className="flex min-w-0  items-center justify-between gap-1">
                            <span
                              className={classNames(
                                'typography-product-body overflow-hidden text-ellipsis whitespace-nowrap',
                                invert
                                  ? !selectedVenue?.address
                                    ? 'text-color-text-lightmode-placeholder'
                                    : 'text-color-text-lightmode-invert'
                                  : '',
                              )}
                            >
                              {displayVenueTitle}
                            </span>
                            <span>
                              <ChevronDown
                                className={classNames(
                                  'h-5 w-5 transition-transform duration-300',
                                  open && 'rotate-180',
                                  invert && 'text-color-text-lightmode-placeholder',
                                )}
                              />
                            </span>
                          </div>
                        )}
                      </Listbox.Button>
                      <Transition
                        show={open}
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options
                          className={classNames(
                            'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-color-bg-lightmode-primary py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-color-bg-darkmode-primary sm:text-sm',
                            invert &&
                              '!bg-color-bg-darkmode-primary shadow-[0px_4px_20px_0px_rgba(255,255,255,0.16)]',
                          )}
                        >
                          {venues?.map((venue) => (
                            <Listbox.Option
                              key={venue.id}
                              className={({ active }) =>
                                classNames(
                                  active ? 'bg-black text-white' : 'text-gray-900',
                                  'relative cursor-pointer select-none px-4 py-2',
                                  invert && '!text-color-text-lightmode-invert',
                                )
                              }
                              value={venue.id}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={'mr-3  inline-block'}>
                                    {venue.address}
                                    {venue.sourceType === VenueSourceType.Roster && (
                                      <span
                                        className={classNames(
                                          'ml-2 inline-block rounded-md px-2 py-0.5 text-xs font-medium',
                                          active
                                            ? 'bg-white/20 text-white'
                                            : 'bg-gray-100 text-gray-700',
                                        )}
                                      >
                                        Club Venue
                                      </span>
                                    )}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        'absolute inset-y-0 right-0 flex items-center pr-2',
                                        active ? 'text-white' : 'text-black',
                                        invert && '!text-white',
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                          {venues?.length > 0 && (
                            <Listbox.Option value={''} key={'addLocation'}>
                              <span
                                onClick={onAddVenue}
                                className={
                                  'typography-product-button-label-medium flex cursor-pointer items-center gap-1 px-2 py-2 text-left text-color-brand-primary'
                                }
                              >
                                <PlusPlain className="h-5 w-5" />
                                Add Location
                              </span>
                            </Listbox.Option>
                          )}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </>
                )}
              </Listbox>
            )}
          />
          {venueFieldError && (
            <span className="typography-product-text-card text-color-error">
              {venueFieldError?.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleItem;
